// utils/soundEffects.js
import { Howl } from 'howler';

// Sound toggle state
let isSoundEnabled = true;
let soundListeners = [];

// Initialize sounds with local files
const sounds = {
  click: null,
  loading: null,
  success: null,
  error: null,
  whisper: null,
  thunder: null,
  organ: null,
  unlock: null,
  pageLoad: null
};

let currentLoadingSound = null;
let currentOrganSound = null;

// Initialize all sounds
export const initSounds = () => {
  // Click sound
  sounds.click = new Howl({
    src: ['/sounds/click.mp3'],
    volume: 0.5,
    preload: true
  });
  
sounds.entrybill = new Howl({
    src: ['/sounds/loginauth.mp3'],
    volume: 0.9,
    preload: true
  });

  // Loading ambient sound
  sounds.loading = new Howl({
    src: ['/sounds/ambient.mp3'],
    volume: 0.15,
    loop: true,
    preload: true
  });
  
  // Success fanfare
  sounds.success = new Howl({
    src: ['/sounds/success.mp3'],
    volume: 0.5,
    preload: true
  });
  
  // Error sound
  sounds.error = new Howl({
    src: ['/sounds/error.mp3'],
    volume: 0.9,
    preload: true
  });
  
  // Whisper/eerie wind
  sounds.whisper = new Howl({
    src: ['/sounds/whisper.mp3'],
    volume: 0.3,
    loop: true,
    preload: true
  });
  
  // Thunder sound
  // sounds.thunder = new Howl({
  //   src: ['/sounds/thunder.mp3'],
  //   volume: 0.4,
  //   preload: true
  // });
  
  // Organ music
  sounds.organ = new Howl({
    src: ['/sounds/organ.mp3'],
    volume: 0.3,
    loop: true,
    preload: true
  });
  
  // Unlock sound
  sounds.unlock = new Howl({
    src: ['/sounds/unlock.mp3'],
    volume: 0.4,
    preload: true
  });
  
  // Page load sound
  sounds.pageLoad = new Howl({
    src: ['/sounds/page-load.mp3'],
    volume: 0.4,
    preload: true
  });
};

// Check if sound is enabled
export const isSoundActive = () => isSoundEnabled;

// Toggle sound on/off
export const toggleSound = () => {
  isSoundEnabled = !isSoundEnabled;
  
  // Stop all playing sounds when toggling off
  if (!isSoundEnabled) {
    stopAllSounds();
  }
  
  // Notify listeners
  soundListeners.forEach(listener => listener(isSoundEnabled));
  
  // Save preference to localStorage
  localStorage.setItem('soundEnabled', isSoundEnabled);
  
  return isSoundEnabled;
};

// Add sound toggle listener
export const onSoundToggle = (callback) => {
  soundListeners.push(callback);
  return () => {
    soundListeners = soundListeners.filter(cb => cb !== callback);
  };
};

// Load saved preference
export const loadSoundPreference = () => {
  const saved = localStorage.getItem('soundEnabled');
  if (saved !== null) {
    isSoundEnabled = saved === 'true';
  }
  return isSoundEnabled;
};

// Play click sound (for buttons, links, etc.)
export const playClickSound = () => {
  if (!isSoundEnabled || !sounds.click) return;
  sounds.click.play();
};

// Entry and Login bill sound
export const playBillSound = () => {
  if (!isSoundEnabled || !sounds.entrybill) return;
  sounds.entrybill.play();
};

// Play loading sound with fade in
export const playLoadingSound = () => {
  if (!isSoundEnabled || !sounds.loading) return;
  if (currentLoadingSound && currentLoadingSound.playing()) {
    return;
  }
  currentLoadingSound = sounds.loading;
  sounds.loading.play();
};

// Stop loading sound with fade out
export const stopLoadingSound = () => {
  if (sounds.loading && sounds.loading.playing()) {
    sounds.loading.fade(0.15, 0, 500);
    setTimeout(() => {
      if (sounds.loading.playing()) {
        sounds.loading.stop();
      }
    }, 500);
  }
};

// Play success sound
export const playSuccessSound = () => {
  if (!isSoundEnabled || !sounds.success) return;
  sounds.success.play();
};

// Play error sound
export const playErrorSound = () => {
  if (!isSoundEnabled || !sounds.error) return;
  sounds.error.play();
};

// Play whisper/eerie sound (ambient background)
export const playWhisperSound = () => {
  if (!isSoundEnabled || !sounds.whisper) return;
  if (!sounds.whisper.playing()) {
    sounds.whisper.play();
  }
};

// Stop whisper sound
export const stopWhisperSound = () => {
  if (sounds.whisper && sounds.whisper.playing()) {
    sounds.whisper.stop();
  }
};

// Play thunder sound
// export const playThunderSound = () => {
//   if (!isSoundEnabled || !sounds.thunder) return;
//   sounds.thunder.play();
// };

// Play organ sound (toggle)
export const playOrganSound = () => {
  if (!isSoundEnabled || !sounds.organ) return;
  if (currentOrganSound && currentOrganSound.playing()) {
    currentOrganSound.fade(0.2, 0, 500);
    setTimeout(() => {
      if (currentOrganSound && currentOrganSound.playing()) {
        currentOrganSound.stop();
      }
      currentOrganSound = null;
    }, 500);
  } else {
    currentOrganSound = sounds.organ;
    sounds.organ.play();
  }
};

// Stop organ sound
export const stopOrganSound = () => {
  if (sounds.organ && sounds.organ.playing()) {
    sounds.organ.fade(0.2, 0, 500);
    setTimeout(() => {
      if (sounds.organ && sounds.organ.playing()) {
        sounds.organ.stop();
      }
    }, 500);
  }
};

// Play unlock sound
export const playUnlockSound = () => {
  if (!isSoundEnabled || !sounds.unlock) return;
  sounds.unlock.play();
};

// Play page load sound
export const playPageLoadSound = () => {
  if (!isSoundEnabled || !sounds.pageLoad) return;
  sounds.pageLoad.play();
};

// Stop all sounds
export const stopAllSounds = () => {
  Object.values(sounds).forEach(sound => {
    if (sound && sound.playing) {
      sound.stop();
    }
  });
  currentLoadingSound = null;
  currentOrganSound = null;
};

// Volume control
export const setMasterVolume = (volume) => {
  const vol = Math.max(0, Math.min(1, volume));
  Object.values(sounds).forEach(sound => {
    if (sound && sound.volume) {
      sound.volume(vol);
    }
  });
};

// Initialize sounds when the app loads
initSounds();
loadSoundPreference();

// Page visibility handling - pause sounds when tab is hidden
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    // Pause all sounds when tab is hidden
    Object.values(sounds).forEach(sound => {
      if (sound && sound.playing && sound.playing()) {
        sound.pause();
      }
    });
  } else {
    // Resume? Optional - resume based on user preference
  }
});